import React, { useRef, useCallback } from 'react';
import FloatingScrollButtons from '@/big-political-list/components/shared/FloatingScrollButtons';

const ROW_HEIGHT = 40;
const VISIBLE_BUFFER = 10;

export default function StationTable({ stations, onRowClick }) {
  const containerRef = useRef(null);
  const [scrollTop, setScrollTop] = React.useState(0);
  const [containerHeight, setContainerHeight] = React.useState(600);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      setContainerHeight(entries[0].contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  const totalHeight = stations.length * ROW_HEIGHT;
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - VISIBLE_BUFFER);
  const endIndex = Math.min(stations.length, Math.ceil((scrollTop + containerHeight) / ROW_HEIGHT) + VISIBLE_BUFFER);
  const visibleStations = stations.slice(startIndex, endIndex);
  const offsetY = startIndex * ROW_HEIGHT;

  const columns = [
    { key: 'state', label: 'State', width: 'w-20' },
    { key: 'market', label: 'Market', width: 'min-w-[200px]' },
    { key: 'station', label: 'Station', width: 'w-28' },
    { key: 'affiliate', label: 'Affiliate', width: 'w-24' },
    { key: 'rep_firm', label: 'Rep Firm', width: 'w-28' },
    { key: 'rep', label: 'Rep', width: 'min-w-[140px]' },
    { key: 'rep_email', label: 'Rep Email', width: 'min-w-[180px]' },
  ];

  return (
    <div className="relative border rounded-lg bg-card overflow-hidden">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-navy text-white">
        <div className="flex">
          {columns.map(col => (
            <div key={col.key} className={`${col.width} shrink-0 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider`}>
              {col.label}
            </div>
          ))}
        </div>
      </div>

      {/* Virtualized Body */}
      <div
        ref={containerRef}
        className="overflow-auto custom-scrollbar"
        style={{ height: 'calc(100vh - 420px)', minHeight: '300px' }}
        onScroll={handleScroll}
      >
        <div style={{ height: totalHeight, position: 'relative' }}>
          <div style={{ transform: `translateY(${offsetY}px)` }}>
            {visibleStations.map((s, i) => (
              <div
                key={s.id}
                onClick={() => onRowClick(s)}
                className={`flex cursor-pointer transition-colors hover:bg-primary/5 ${(startIndex + i) % 2 === 0 ? 'bg-card' : 'bg-muted/30'}`}
                style={{ height: ROW_HEIGHT }}
              >
                {columns.map(col => (
                  <div key={col.key} className={`${col.width} shrink-0 px-3 flex items-center text-sm truncate`}>
                    {s[col.key] || <span className="text-muted-foreground">—</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <FloatingScrollButtons containerRef={containerRef} />
      </div>

      {/* Footer */}
      <div className="border-t px-3 py-2 text-xs text-muted-foreground bg-card">
        {stations.length.toLocaleString()} station{stations.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}